/**
 * AuditHistoryChart — compliance % over time per customer or site.
 *
 * Renders one <Line> per series returned by /api/reports/audit-history.
 * X axis is the audit's `updated_at` timestamp (parsed to a numeric
 * unix-ms so Recharts can lay it out proportionally, not evenly-spaced
 * like a category axis would). Y axis is 0-100 with a 75%/85%
 * reference line grid so a customer can spot audits that dipped
 * below "Satisfactory" at a glance.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { LineChart as LineChartIcon } from "lucide-react";

// Distinctive per-series colors. Recharts cycles them modulo the
// length, so the first 8 series (which is what the backend returns)
// each get a unique color. Deliberately warm/cool balanced so the
// chart still reads with 5+ overlapping lines.
const SERIES_COLORS = [
  "#3B82F6", // blue-500
  "#EF4444", // red-500
  "#10B981", // emerald-500
  "#F59E0B", // amber-500
  "#8B5CF6", // violet-500
  "#EC4899", // pink-500
  "#14B8A6", // teal-500
  "#F97316", // orange-500
];

function fmtDate(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  } catch {
    return String(ts).slice(0, 10);
  }
}

export default function AuditHistoryChart() {
  const [groupBy, setGroupBy] = useState("customer");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    api.get(`/reports/audit-history?group_by=${groupBy}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [groupBy]);

  // Recharts wants a flat rows array with one column per series. We
  // build the union set of timestamps (numeric ms so the axis is
  // proportional), then for each row pin every series' pct — leaving
  // a series column undefined at timestamps where THAT series has no
  // audit so Recharts renders a gap instead of a spurious 0.
  const { rows, seriesNames } = useMemo(() => {
    const series = data?.series || [];
    const names = series.map((s) => s.name);
    const allDates = new Map(); // ts_ms -> { ts, [name]: pct, ... }
    series.forEach((s) => {
      s.points.forEach((p) => {
        const ms = new Date(p.date).getTime();
        if (Number.isNaN(ms)) return;
        const row = allDates.get(ms) || { ts: ms };
        row[s.name] = p.pct;
        allDates.set(ms, row);
      });
    });
    return {
      rows: Array.from(allDates.values()).sort((a, b) => a.ts - b.ts),
      seriesNames: names,
    };
  }, [data]);

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="audit-history-chart">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-2 rounded-md border border-blue-900 bg-blue-950/40 text-blue-400 shrink-0">
              <LineChartIcon className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">
                Audit Compliance Over Time
              </div>
              <div className="font-display font-bold text-lg tracking-tight">
                By {groupBy === "customer" ? "Customer" : "Site"}
                {data?.total_series > (data?.series_count || 0) && (
                  <span className="ml-2 text-[11px] text-slate-500 font-normal italic">
                    (top 8 of {data.total_series})
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-1" role="tablist" aria-label="Group audits by">
            <Button
              size="sm"
              variant={groupBy === "customer" ? "default" : "outline"}
              onClick={() => setGroupBy("customer")}
              className={groupBy === "customer"
                ? "bg-blue-700 hover:bg-blue-600 text-white h-8"
                : "border-slate-700 bg-slate-950 hover:bg-slate-800 text-slate-300 h-8"}
              data-testid="audit-history-group-customer"
            >
              Customer
            </Button>
            <Button
              size="sm"
              variant={groupBy === "site" ? "default" : "outline"}
              onClick={() => setGroupBy("site")}
              className={groupBy === "site"
                ? "bg-blue-700 hover:bg-blue-600 text-white h-8"
                : "border-slate-700 bg-slate-950 hover:bg-slate-800 text-slate-300 h-8"}
              data-testid="audit-history-group-site"
            >
              Site
            </Button>
          </div>
        </div>

        {err ? (
          <div className="text-sm text-red-400 py-8 text-center" data-testid="audit-history-error">{err}</div>
        ) : data === null ? (
          <div className="text-sm text-slate-500 py-8 text-center" data-testid="audit-history-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-400 py-8 text-center" data-testid="audit-history-empty">
            No audits yet. Compliance % will chart here after the first Site Audit form is completed.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={rows} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
              <XAxis
                dataKey="ts"
                type="number"
                domain={["dataMin", "dataMax"]}
                scale="time"
                tickFormatter={fmtDate}
                stroke="#94A3B8"
                fontSize={11}
              />
              <YAxis
                domain={[0, 100]}
                stroke="#94A3B8"
                fontSize={11}
                tickFormatter={(v) => `${v}%`}
              />
              {/* Threshold lines match the PDF's status labels
                  (Excellent ≥95, Satisfactory ≥85, Needs Improvement
                  ≥75). Users can eyeball how many audits crossed the
                  "Unsatisfactory" line without reading numbers. */}
              <ReferenceLine y={85} stroke="#F59E0B" strokeDasharray="4 4" strokeOpacity={0.5} />
              <ReferenceLine y={75} stroke="#EF4444" strokeDasharray="4 4" strokeOpacity={0.5} />
              <Tooltip
                contentStyle={{ background: "#0F172A", border: "1px solid #334155", color: "#F1F5F9", fontSize: 12 }}
                cursor={{ stroke: "#334155" }}
                labelFormatter={(v) => fmtDate(v)}
                formatter={(value, name) => [`${value}%`, name]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {seriesNames.map((name, idx) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={SERIES_COLORS[idx % SERIES_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
